import { z } from "zod";
/**
 * A calendar date on the site's clock, as the tariff table stores it. Checked
 * as a real date, because Postgres rejects 2026-02-30 with an error the caller
 * would otherwise see as a 500 rather than a 400.
 */
export const LocalDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Use the form YYYY-MM-DD.")
    .refine((value) => {
    const [year, month, day] = value.split("-").map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return (year >= 2000 && year <= 2100 && date.getUTCMonth() === month - 1 && date.getUTCDate() === day);
}, "Give a real date between 2000 and 2100.");
/**
 * One rate and the day it took effect (docs/DECISIONS/0033). A tariff runs
 * until the next one's `effectiveFrom`; the last has no end.
 */
export const Tariff = z.object({
    id: z.string().uuid(),
    effectiveFrom: LocalDate,
    /** The next tariff's start, or null for the one in force now. */
    effectiveTo: LocalDate.nullable(),
    /** Per kWh, in the site's currency, as the operator's bill states it. */
    ratePerKwh: z.number(),
    vatInclusive: z.boolean(),
    vatPercent: z.number(),
    /** What a kWh actually costs: the rate, plus VAT when the rate excludes it. */
    pricePerKwh: z.number(),
    createdAt: z.string().datetime(),
});
export const TariffPage = z.object({
    tariffs: z.array(Tariff),
    /** Pass as `before` for the next, older page; null on the last. */
    nextBefore: LocalDate.nullable(),
});
/** Newest first, in pages, as every list that can grow must be (section 10). */
export const TariffQuery = z
    .object({
    limit: z.coerce.number().int().min(1).max(500).default(100),
    before: LocalDate.optional(),
})
    .strict();
export const TariffRequest = z
    .object({
    effectiveFrom: LocalDate,
    /**
     * Stored to four decimal places, so the smallest rate is 0.0001: a smaller
     * one would round to zero and be refused by the database as a 500.
     */
    ratePerKwh: z.number().min(0.0001).max(1_000_000),
    vatInclusive: z.boolean(),
    vatPercent: z.number().min(0).max(100).default(7.5),
})
    .strict();
export const TariffParams = z.object({ id: z.string().uuid() }).strict();
//# sourceMappingURL=tariffs.js.map